import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { FileRecord } from "@/shared/domain/files";

import { fileQueryKeys } from "./fileQueryKeys";

type DeleteFileMutationArgs = {
  file: FileRecord;
  /** Required so cache invalidation can target the owning org. */
  resourceOwnerId: string;
};

export function useDeleteFileMutation() {
  const { files: fileService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<void, unknown, DeleteFileMutationArgs>({
    mutationFn: ({ file }) => {
      return fileService.deleteFile(file.file_id);
    },
    onSuccess: (_void, variables) => {
      void queryClient.invalidateQueries({
        queryKey: fileQueryKeys.lists(variables.resourceOwnerId),
      });
    },
  });
}

/**
 * Bulk delete files by id.
 */
export function useDeleteFilesMutation() {
  const { files: fileService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<
    void,
    unknown,
    { fileIds: string[]; resourceOwnerId: string }
  >({
    mutationFn: ({ fileIds }) => fileService.deleteFiles(fileIds),
    // Use `onSettled` rather than `onSuccess`: `deleteFiles` fans out via
    // `Promise.all`, so a single per-id rejection rejects the whole batch
    // even though earlier ids may have been removed server-side. Invalidating
    // only on success would leave the list showing rows that no longer
    // exist until the user reloads the page.
    onSettled: (_void, _error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: fileQueryKeys.lists(variables.resourceOwnerId),
      });
    },
  });
}
